// useSearch — debounced autocomplete against /v1/search. Cancels the
// previous in-flight request when the (debounced) query, sort, or source
// scope changes so a fast typer / rapid pill toggle never sees stale results
// land after a fresher one.
//
// Ranking, platform scoping, and dedup are the server's job: `sort` and
// `sources` are forwarded so a sort-button or platform-pill change re-queries
// the full match set instead of reshuffling one already-fetched page.
//
// Empty / sub-2-char queries short-circuit to an empty result set; the
// component above decides whether to render anything at all.

import { useEffect, useRef, useState } from 'react'
import { type SortParam, search } from '../api/search'
import type { SearchHit, TradeSummary } from '../api/types'
import { tradeCarriesValuation } from '../lib/liveFeedGate'
import { applyTradeToSearchHits } from '../lib/liveTradeRows'
import { mergeTokenBirth, rememberEarliestTokenBirth } from '../lib/tokenAge'
import { useDebouncedValue } from './useDebouncedValue'
import { useWsSubscription } from './useWsSubscription'

export interface UseSearchResult {
  results: SearchHit[]
  loading: boolean
  error: Error | null
}

interface LaunchpadBirthWs {
  jetton_master: string
  deploy_ts?: string
}

export function useSearch(
  query: string,
  sort: SortParam = 'relevance',
  sources = '',
  limit = 30
): UseSearchResult {
  const trimmed = query.trim()
  const debounced = useDebouncedValue(trimmed, 25)
  const [results, setResults] = useState<SearchHit[]>([])
  const [answeredFor, setAnsweredFor] = useState('')
  const answeredForRef = useRef('')
  const bufferedByRequest = useRef(new Map<string, TradeSummary[]>())
  const seenByRequest = useRef(new Map<string, Set<string>>())
  const coveredByRequest = useRef(new Map<string, Set<string>>())
  const rememberedBirthsRef = useRef(new Map<string, string>())
  const [error, setError] = useState<Error | null>(null)
  const viewedRequest = searchRequestKey(trimmed, sort, sources, limit)

  // `loading` answers exactly one question — "do `results` describe the query the
  // user is looking at?" — because a caller uses it to decide whether an empty
  // result set means "not found" or "not yet".
  //
  // Anything narrower has gaps. A plain `fetching` flag is false for the 50ms
  // the newest keystrokes spend in the debounce. Adding "debounce is settling"
  // still leaves one commit uncovered: when the debounce timer fires, `debounced`
  // updates and the render happens BEFORE the effect that starts the request, so
  // for that frame nothing is settling and nothing is fetching, while `results`
  // is still empty. Search rendered its not-found fallback in that single frame.
  //
  // Comparing against the full request the current `results` were fetched for
  // has no such seam: query, sort, source scope, and limit all own the response.
  const ownsViewedRequest = answeredFor === viewedRequest
  const loading = trimmed.length >= 2 && !ownsViewedRequest

  // Subscribe before the HTTP seed resolves. Trades received during the seed
  // are replayed through the seed's exact price cursor, so the first owned
  // Search frame already contains the live tail and a sibling pool is untouched.
  useWsSubscription<TradeSummary | LaunchpadBirthWs>(
    trimmed.length >= 2 ? 'discover' : null,
    (msg) => {
      if (msg.event === 'launchpad.deploy') {
        const birth = msg.data as LaunchpadBirthWs
        const birthTS = rememberEarliestTokenBirth(
          rememberedBirthsRef.current,
          birth.jetton_master,
          birth.deploy_ts
        )
        if (!birthTS) return
        setResults((current) => patchSearchBirth(current, birth.jetton_master, birthTS))
        return
      }
      if (msg.event !== 'trade') return
      const trade = msg.data as TradeSummary
      const requestKey = viewedRequest
      if (coveredByRequest.current.get(requestKey)?.has(searchCoverageKey(trade))) return
      if (!rememberSearchTrade(seenByRequest.current, requestKey, trade)) return
      if (answeredForRef.current === requestKey) {
        setResults((current) => applyTradeToSearchHits(current, trade))
        return
      }
      bufferSearchTrade(bufferedByRequest.current, requestKey, trade)
    }
  )

  useEffect(() => {
    // A modal session owns one visible request. Do not retain replay buffers or
    // dedup sets for every query the user has typed.
    for (const key of bufferedByRequest.current.keys()) {
      if (key !== viewedRequest) bufferedByRequest.current.delete(key)
    }
    for (const key of seenByRequest.current.keys()) {
      if (key !== viewedRequest) seenByRequest.current.delete(key)
    }
    for (const key of coveredByRequest.current.keys()) {
      if (key !== viewedRequest) coveredByRequest.current.delete(key)
    }
  }, [viewedRequest])

  useEffect(() => {
    if (debounced.length < 2) {
      setResults([])
      const responseKey = searchRequestKey(debounced, sort, sources, limit)
      answeredForRef.current = responseKey
      setAnsweredFor(responseKey)
      setError(null)
      return
    }
    const responseKey = searchRequestKey(debounced, sort, sources, limit)
    const controller = new AbortController()
    setError(null)
    void search(debounced, { limit, sort, sources }, { signal: controller.signal })
      .then((seeded) => {
        if (controller.signal.aborted) return
        const items = patchRememberedSearchBirths(
          replaySearchSeed(seeded.items, bufferedByRequest.current.get(responseKey) ?? []),
          rememberedBirthsRef.current
        )
        coveredByRequest.current.set(responseKey, searchCoveredKeys(seeded.items))
        bufferedByRequest.current.delete(responseKey)
        answeredForRef.current = responseKey
        setResults(items)
        setAnsweredFor(responseKey)
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        // AbortError surfaces as DOMException on some runtimes — ignore.
        if (e instanceof DOMException && e.name === 'AbortError') return
        setError(e instanceof Error ? e : new Error(String(e)))
        // The query got an answer, even if it was an error: stop claiming the
        // search is still in flight or the modal spins forever.
        setAnsweredFor(responseKey)
      })
    return () => controller.abort()
  }, [debounced, sort, sources, limit])

  return {
    results: ownsViewedRequest ? results : [],
    loading,
    error: ownsViewedRequest ? error : null,
  }
}

function searchTradeIdentity(trade: TradeSummary): string {
  return [trade.pool_addr, trade.ts, trade.lt ?? '', trade.tx_hash ?? '', trade.side].join('|')
}

function rememberSearchTrade(
  seenByRequest: Map<string, Set<string>>,
  requestKey: string,
  trade: TradeSummary
): boolean {
  const identity = searchTradeIdentity(trade)
  if (!identity) return true
  let seen = seenByRequest.get(requestKey)
  if (!seen) {
    seen = new Set<string>()
    seenByRequest.set(requestKey, seen)
  }
  // Stage-3 B1: the valued patch shares the pending frame's identity. It may
  // pass the dedupe exactly once — applyTradeToSearchHit then adds exactly
  // what the pending pass could not (the USD share and the valuation).
  const valued = tradeCarriesValuation(trade)
  const valuedIdentity = `${identity}|valued`
  if (seen.has(valued ? valuedIdentity : identity)) return false
  seen.add(identity)
  if (valued) seen.add(valuedIdentity)
  return true
}

function bufferSearchTrade(
  bufferedByRequest: Map<string, TradeSummary[]>,
  requestKey: string,
  trade: TradeSummary
): void {
  const buffered = bufferedByRequest.get(requestKey) ?? []
  buffered.push(trade)
  bufferedByRequest.set(requestKey, buffered)
}

function replaySearchSeed(seed: SearchHit[], buffered: TradeSummary[]): SearchHit[] {
  const covered = searchCoveredKeys(seed)
  let replayed = seed
  for (const trade of buffered) {
    if (covered.has(searchCoverageKey(trade))) continue
    replayed = applyTradeToSearchHits(replayed, trade)
  }
  return replayed
}

function searchCoveredKeys(seed: SearchHit[]): Set<string> {
  const covered = new Set<string>()
  for (const hit of seed) {
    for (const key of hit.covered_trade_keys ?? []) {
      covered.add(`${hit.pool_addr ?? ''}\u0000${key}`)
    }
  }
  return covered
}

function searchCoverageKey(trade: TradeSummary): string {
  return `${trade.pool_addr ?? ''}\u0000${Math.trunc(trade.ts)}:${trade.tx_hash}`
}

function patchSearchBirth(hits: SearchHit[], jettonMaster: string, birthTS: string): SearchHit[] {
  let changed = false
  const next = hits.map((hit) => {
    if (hit.jetton_master !== jettonMaster) return hit
    const merged = mergeTokenBirth(hit, { birth_ts: birthTS })
    if (merged === hit) return hit
    changed = true
    return merged
  })
  return changed ? next : hits
}

function patchRememberedSearchBirths(
  hits: SearchHit[],
  births: ReadonlyMap<string, string>
): SearchHit[] {
  let next = hits
  for (const [jettonMaster, birthTS] of births) {
    next = patchSearchBirth(next, jettonMaster, birthTS)
  }
  return next
}

function searchRequestKey(query: string, sort: SortParam, sources: string, limit: number): string {
  return JSON.stringify([query, sort, sources, limit])
}
